import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { select } from '@ngxs/store';
import { AccountStore, OlAccountGates, OlMode, provideOlAccount, provideOlUserMenuItems } from '@ol/kit';
import { UserState } from '@core/store/user/user.state';
import { HubAccountStore } from './hub-account.store';
import { OlAccountAuthAdapter } from './ol-account-auth.adapter';
import { buildOlAccountConfigBase } from './ol-account-config.factory';
import { HUB_SETTINGS_SECTIONS, settingsEntrySectionId } from './hub-settings.sections';

// Rows injected into <ol-header>'s built-in <ol-user-dropdown> between
// "User Profile" and "Log out". Each navigates to {accountBasePath}/{target};
// the dropdown reads accountBasePath from OL_ACCOUNT_CONFIG, so it resolves to
// /account/*. "Account Settings" → the licenses section (kit built-in);
// "Settings" → the first hub section available in the current edition (bridges
// on cloud; the kit registers routes for every section regardless of edition, so
// a hardcoded cloud-only target would land enterprise/lite users on a section
// the shell hides).
const HUB_USER_MENU_ITEMS = [
  { id: 'account-settings', label: 'Account Settings', icon: 'icon-profile', target: 'licenses', order: 1 },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'icon-settings',
    target: settingsEntrySectionId(HUB_SETTINGS_SECTIONS),
    order: 2,
  },
];

// Everything this gate hides - group CRUD, group membership, invite and the member row actions -
// is tenant-admin only server-side, so ungated the Team view offers members buttons that only 403.
// Hub ships no translations, so the user dropdown's built-in Language row stays hidden.
export const HUB_ACCOUNT_GATES: OlAccountGates = {
  canManageTeam: () => select(UserState.isAdminUser),
  showLanguagePicker: () => false,
};

// Wires the @ol/kit account feature for a host shell: the account config
// (mode + base path), the auth adapter, and the custom user-menu rows.
// Shared by the standalone AppModule and the embedded AppEmbedModule so the
// <ol-header> user dropdown renders (and resolves AccountStore) in both builds.
export function provideHubAccount(mode: OlMode, accountBasePath: string): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideOlAccount({
      auth: OlAccountAuthAdapter,
      config: { ...buildOlAccountConfigBase(), mode, accountBasePath },
      gates: HUB_ACCOUNT_GATES,
    }),
    provideOlUserMenuItems(HUB_USER_MENU_ITEMS),
    // Change-password must end the current session (see HubAccountStore).
    { provide: AccountStore, useClass: HubAccountStore },
  ]);
}
